/* Charmance — Builder preview capture, v1.0
 * Load only on pages containing the Charm Bar Builder.
 * Captures original DOM preview and persists minimal positions in Shopify line item properties.
 */
(() => {
  'use strict';
  if (window.__charmanceBuilderPreviewInstalled) return;
  window.__charmanceBuilderPreviewInstalled = true;

  const CACHE_KEY = 'charmance_builder_previews_v1';
  const originalFetch = window.fetch;
  const STYLE_PROPS = [
    'display', 'position', 'box-sizing', 'width', 'height',
    'min-width', 'min-height', 'max-width', 'max-height', 'aspect-ratio',
    'top', 'right', 'bottom', 'left', 'inset',
    'transform', 'transform-origin', 'margin', 'padding', 'overflow',
    'object-fit', 'object-position', 'opacity', 'visibility', 'z-index',
    'background-color', 'background-image', 'background-size',
    'background-position', 'border', 'border-radius', 'filter',
    'align-items', 'justify-content', 'flex-direction', 'flex', 'gap'
  ];

  function imageUrl(img) {
    return img?.currentSrc || img?.getAttribute('src') || '';
  }

  function sourcePreview() {
    return [...document.querySelectorAll('.charm_bar__reference')]
      .find(el => !el.classList.contains('cb-builder-preview-modal-clone') &&
        el.getBoundingClientRect().width > 0) || null;
  }

  function normalizeRect(image, referenceBox) {
    const rect = image?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height || !referenceBox.width || !referenceBox.height) return null;
    const values = [
      (rect.left - referenceBox.left) / referenceBox.width,
      (rect.top - referenceBox.top) / referenceBox.height,
      rect.width / referenceBox.width,
      rect.height / referenceBox.height
    ];
    return values.every(Number.isFinite) ? values.map(x => Number(x.toFixed(5))).join(',') : null;
  }

  function capture() {
    const source = sourcePreview();
    if (!source) return null;

    const bounds = source.getBoundingClientRect();
    // getBoundingClientRect() includes zoom/scale from the sticky mobile Builder.
    // The cloned DOM itself uses untransformed CSS pixels, so keep its intrinsic
    // dimensions. Otherwise the base/charms get clipped when displayed in cart.
    const sourceWidth = source.offsetWidth || bounds.width;
    const sourceHeight = source.offsetHeight || bounds.height;
    const baseImg = source.querySelector('.charm_bar__base-product-image');
    const baseSrc = imageUrl(baseImg);
    const charms = {};

    source.querySelectorAll('[data-preview-charm="true"]').forEach(node => {
      const index = node.getAttribute('data-point-index');
      const img = node.querySelector('img.charm_bar__charm-image, img');
      const src = imageUrl(img);
      const rect = normalizeRect(img, bounds);
      if (index !== null && src && rect) charms[String(index)] = { src, rect };
    });

    // A style-inlined HTML clone provides pixel-fidelity in the same browser;
    // the compact Shopify line properties below remain the durable fallback.
    const clone = source.cloneNode(true);
    const oldNodes = [source, ...source.querySelectorAll('*')];
    const newNodes = [clone, ...clone.querySelectorAll('*')];
    oldNodes.forEach((original, index) => {
      const target = newNodes[index];
      if (!target) return;
      const cs = getComputedStyle(original);
      for (const name of STYLE_PROPS) {
        const value = cs.getPropertyValue(name);
        if (value) target.style.setProperty(name, value);
      }
      if (original.tagName === 'IMG') {
        const src = imageUrl(original);
        if (src) target.setAttribute('src', src);
      }
    });
    clone.classList.add('cb-builder-preview-modal-clone');
    clone.querySelectorAll('script, iframe, form, button, .charm_bar__point, .charm_bar__drop-area, .charm-bar-fullscreen-button, [data-drop-target]').forEach(el => el.remove());
    clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
    clone.querySelectorAll('draggable-element').forEach(el => {
      const div = document.createElement('div');
      // IMPORTANT: keep data-preview-charm and data-point-index.
      // The cart uses these to associate every charm with its Shopify line.
      // Without them cachedHtml() discards every charm placeholder.
      for (const attribute of Array.from(el.attributes)) {
        if (attribute.name !== 'is') {
          div.setAttribute(attribute.name, attribute.value);
        }
      }
      while (el.firstChild) div.appendChild(el.firstChild);
      el.replaceWith(div);
    });
    clone.querySelectorAll('*').forEach(el => {
      el.removeAttribute('draggable');
      el.removeAttribute('tabindex');
      el.style.pointerEvents = 'none';
      el.style.animation = 'none';
      el.style.transition = 'none';
    });
    // The Builder can temporarily hide the base while cloning; force visible.
    [clone, ...clone.querySelectorAll('.charm_bar__reference-media, .charm_bar__base-product-image')]
      .forEach(el => el.style.setProperty('visibility', 'visible', 'important'));
    clone.style.position = 'relative';
    clone.style.transform = 'none';
    clone.style.margin = '0';
    clone.style.width = sourceWidth + 'px';
    clone.style.height = sourceHeight + 'px';
    clone.style.maxWidth = 'none';
    clone.style.maxHeight = 'none';

    return {
      baseSrc,
      charms,
      html: clone.outerHTML,
      width: Math.round(sourceWidth),
      height: Math.round(sourceHeight),
      savedAt: Date.now()
    };
  }

  function getCache() {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); }
    catch { return {}; }
  }

  function cacheBundle(id, captured) {
    if (!captured) return;
    try {
      const cache = getCache();
      cache[id] = captured;
      // Keep storage small; don't make adding to cart depend on storage quota.
      const ids = Object.keys(cache).sort((a, b) =>
        (cache[b].savedAt || 0) - (cache[a].savedAt || 0));
      for (const old of ids.slice(20)) delete cache[old];
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.warn('[Charmance Preview] Local cache unavailable; Shopify fallback still works.', e);
    }
  }

  window.fetch = function (...args) {
    const [input, options] = args;
    const url = typeof input === 'string' ? input : input?.url || '';
    const isAdd = /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(url);
    if (!isAdd || typeof options?.body !== 'string' || !document.querySelector('charm-bar-builder')) {
      return originalFetch.apply(this, args);
    }

    let bundleId = null;
    let captured = null;
    try {
      const data = JSON.parse(options.body);
      const items = data?.items;
      const base = items?.find(item => item.properties?.['_Item Type'] === 'Base Product');
      bundleId = base?.properties?.['_Charm Bar Bundle ID'];
      if (bundleId && Array.isArray(items)) {
        captured = capture();
        if (captured) {
          for (const item of items) {
            if (item.properties?.['_Charm Bar Bundle ID'] !== bundleId) continue;
            const type = item.properties['_Item Type'];
            if (type === 'Base Product') {
              if (captured.baseSrc) item.properties['_CB Preview Base'] = captured.baseSrc;
            } else if (type === 'Charm') {
              const anchor = String(item.properties['Anchor Point Number'] ?? item.properties['_Anchor Point Number'] ?? '');
              const slot = captured.charms[anchor];
              if (slot) {
                item.properties['_CB Preview Image'] = slot.src;
                item.properties['_CB Preview Rect'] = slot.rect;
              }
            }
          }
          options.body = JSON.stringify(data);
        }
      }
    } catch (e) {
      console.warn('[Charmance Preview] Capture failed (cart add continues):', e);
    }

    const response = originalFetch.apply(this, args);
    return Promise.resolve(response).then(result => {
      if (result.ok && bundleId && captured) {
        cacheBundle(bundleId, captured);
        console.info('[Charmance Preview] Saved for bundle', bundleId);
      }
      return result;
    });
  };
})();
